/**
 * SyncHistory — Inteligência de Dados (Fase 2)
 * -----------------------------------------------------------------------------
 * Histórico das execuções (runs) de importação de dados externos: status,
 * páginas processadas, quantidades inseridas/atualizadas/inalteradas e erros.
 *
 * A paginação é feita no servidor (RPC list_external_import_runs) para não
 * carregar históricos longos de uma vez.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-23 v1.0.0 - Criação inicial (Fase 2 - Dados Externos).
 */

import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  History,
  Loader2,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  ListPageFiltersBar,
  ListPageContent,
  ListPageFooter,
  KpiCard,
} from '@/components/layout/list-page';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { InlineNotice } from '@/components/ui/inline-notice';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useExternalDatasets,
  useExternalImportRuns,
} from '@/hooks/data/useExternalDatasets';

const PAGE_SIZE = 20;

/** Aparência dos status de execução */
const STATUS_META: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  queued: { label: 'Na fila', variant: 'secondary' },
  running: { label: 'Executando', variant: 'outline' },
  completed: { label: 'Concluída', variant: 'default' },
  failed: { label: 'Falhou', variant: 'destructive' },
  cancelled: { label: 'Cancelada', variant: 'secondary' },
};

/** Formata data/hora no padrão pt-BR */
const formatDateTime = (value: string | null) =>
  value ? format(new Date(value), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR }) : '—';

/** Página "Histórico de sincronização" */
export default function SyncHistory() {
  const [datasetFilter, setDatasetFilter] = useState<string>('all');
  const [page, setPage] = useState(0);

  const { datasets } = useExternalDatasets();
  const { runs, totalCount, isLoading } = useExternalImportRuns({
    datasetId: datasetFilter === 'all' ? null : datasetFilter,
    page,
    pageSize: PAGE_SIZE,
  });

  /** KPIs da página atual do histórico */
  const kpis = useMemo(() => {
    const running = runs.filter((r) => r.status === 'running' || r.status === 'queued').length;
    const failed = runs.filter((r) => r.status === 'failed').length;
    const records = runs.reduce((sum, r) => sum + Number(r.total_fetched ?? 0), 0);
    return { running, failed, records };
  }, [runs]);

  const totalPages = Math.max(1, Math.ceil(Number(totalCount || 0) / PAGE_SIZE));

  /** Troca o filtro de dataset e volta para a primeira página */
  const handleFilterChange = (value: string) => {
    setDatasetFilter(value);
    setPage(0);
  };

  return (
    <ListPageLayout>
      <ListPageHeader title="Histórico de sincronização" />

      <ListPageKpis className="border-b-0 pb-1">
        <KpiCard color="orange" icon={Loader2} label="Em andamento" value={kpis.running} />
        <KpiCard color="red" icon={AlertTriangle} label="Com falha" value={kpis.failed} />
        <KpiCard
          color="green"
          icon={CheckCircle2}
          label="Registros nesta página"
          value={kpis.records.toLocaleString('pt-BR')}
        />
      </ListPageKpis>

      <ListPageFiltersBar className="border-b-0 bg-transparent">
        <Select value={datasetFilter} onValueChange={handleFilterChange}>
          <SelectTrigger className="w-[260px]">
            <SelectValue placeholder="Dataset" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os datasets</SelectItem>
            {datasets.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                {d.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ListPageFiltersBar>

      <ListPageContent>
        {isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : runs.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <History className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Nenhuma sincronização registrada</p>
            <p className="text-xs text-muted-foreground">
              Inicie uma importação em Datasets para ver o histórico aqui.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {runs.map((run) => {
              const meta = STATUS_META[run.status] ?? STATUS_META.queued;

              return (
                <div key={run.id} className="space-y-2 rounded-lg border p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">{run.dataset_name}</span>
                      <Badge variant={meta.variant}>{meta.label}</Badge>
                      <Badge variant="outline">{run.mode}</Badge>
                      <span className="font-mono text-[11px] text-muted-foreground">
                        {run.id.slice(0, 8).toUpperCase()}
                      </span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTime(run.started_at ?? run.created_at)}
                    </span>
                  </div>

                  {/* Contadores da execução */}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span>Páginas: {run.page}</span>
                    <span>Buscados: {Number(run.total_fetched ?? 0).toLocaleString('pt-BR')}</span>
                    <span>Novos: {Number(run.inserted ?? 0).toLocaleString('pt-BR')}</span>
                    <span>Atualizados: {Number(run.updated ?? 0).toLocaleString('pt-BR')}</span>
                    <span>Sem mudança: {Number(run.unchanged ?? 0).toLocaleString('pt-BR')}</span>
                    {Number(run.failed ?? 0) > 0 && (
                      <span className="text-destructive">Falhas: {run.failed}</span>
                    )}
                    <span>Fim: {formatDateTime(run.finished_at)}</span>
                  </div>

                  {run.error && <InlineNotice variant="error">{run.error}</InlineNotice>}
                </div>
              );
            })}
          </div>
        )}
      </ListPageContent>

      <ListPageFooter>
        <span className="text-xs text-muted-foreground">
          {Number(totalCount || 0).toLocaleString('pt-BR')} execuções · página {page + 1} de{' '}
          {totalPages}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Próxima
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </ListPageFooter>
    </ListPageLayout>
  );
}
